/**
 * ChronicleSettingsModal — DEV-1340 (epic DEV-1336).
 *
 * Chronicle CONFIGURATION in a modal (cross-pillar target: settings modal =
 * config only; content lives on the detail page). Mirrors MiniProjectSettingsModal.
 *
 * Three tabs, designed so a student sees almost nothing scary:
 *   1. Basics            — title, subtitle, status, owner picker (same logic as
 *                          Mini/Full Projects), and (admin only) "Whose project
 *                          is this?" with the original-project credit inline.
 *   2. Who can see this  — visibility (public / community / private). Parity with
 *                          the Mini/Full Project visibility control.
 *   3. More              — admin/optional: topics (discovery), where-it-happened
 *                          (local conditions), and extra references.
 *
 * Plain language throughout — "native"/"attributed" are never shown to users;
 * the underlying source_type stays in the data model.
 *
 * Transitional bespoke shell — adopts the shared SettingsShell when DEV-1261 lands.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { updateChronicle, deleteChronicle, fetchProxyBeneficiaries, type ChronicleResponse, type UpdateChronicleRequest, type ProxyBeneficiarySummary } from '../../api/chronicles';
import { fetchUserByGuid } from '../../api/security';
import { getIsSystemAdmin } from '../../auth/AuthContext';
import { buildOwnerOptions, type OwnerOption } from '../../Data/Chronicles/ownerOptions';

interface Props {
  chronicle: ChronicleResponse;
  currentUserGuid: string;
  communityEntities?: any[];
  communityRoleAssignments?: any[];
  communityEntityRelationships?: any[];
  onClose: () => void;
  onSaved: () => void;
  /** Called after a successful soft-delete so the parent can navigate away. */
  onDeleted?: () => void;
}

interface SourceRow {
  id: string;
  sourceRole: 'primary' | 'reference';
  sourceOrgName: string;
  sourceTitle: string;
  sourceUrl: string;
  permissionStatus: string;
}
interface ClassificationRow {
  id: string;
  dimension: 'solution' | 'problem' | 'issue' | 'outcome';
  valueKey: string;
}
interface ContextRow {
  id: string;
  contextKey: string;
  contextValue: string;
}

type TabKey = 'basics' | 'visibility' | 'more';

export default function ChronicleSettingsModal({
  chronicle,
  currentUserGuid,
  communityEntities = [],
  communityRoleAssignments = [],
  communityEntityRelationships = [],
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const { t } = useAppTranslation('pages');
  const isAdmin = getIsSystemAdmin();

  // Who may soft-delete: the creator, the credited initiator, or a sysadmin.
  const canDelete = isAdmin
    || (chronicle.createdByGuid || '').toUpperCase() === (currentUserGuid || '').toUpperCase()
    || (chronicle.initiatorGuid || '').toUpperCase() === (currentUserGuid || '').toUpperCase();

  // ── Soft-delete (danger zone) state ──
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delText, setDelText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleSoftDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteChronicle(chronicle.chronicleGuid);
      onClose();
      onDeleted?.();
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
      setDeleting(false);
    }
  };

  const [tab, setTab] = useState<TabKey>('basics');

  const [title, setTitle] = useState(chronicle.title);
  const [subtitle, setSubtitle] = useState(chronicle.subtitle ?? '');
  const [status, setStatus] = useState(chronicle.status);
  const [ownerGuid, setOwnerGuid] = useState((chronicle.ownerGuid || currentUserGuid).toString());
  const [visibility, setVisibility] = useState(chronicle.visibility || 'public');

  const [sourceType, setSourceType] = useState<'native' | 'attributed'>(
    chronicle.sourceType === 'attributed' ? 'attributed' : 'native',
  );
  const [sources, setSources] = useState<SourceRow[]>(
    (chronicle.sources ?? []).map((s, i) => ({
      id: `src-${s.sourceGuid || i}`,
      sourceRole: s.sourceRole === 'primary' ? 'primary' : 'reference',
      sourceOrgName: s.sourceOrgName ?? '',
      sourceTitle: s.sourceTitle ?? '',
      sourceUrl: s.sourceUrl ?? '',
      permissionStatus: s.permissionStatus || 'linked_reference',
    })),
  );
  const [classifications, setClassifications] = useState<ClassificationRow[]>(
    (chronicle.classifications ?? []).map((c, i) => ({
      id: `cls-${c.classificationGuid || i}`,
      dimension: c.dimension as ClassificationRow['dimension'],
      valueKey: c.valueKey ?? '',
    })),
  );
  const [contexts, setContexts] = useState<ContextRow[]>(
    (chronicle.contexts ?? []).map((c, i) => ({
      id: `ctx-${c.contextGuid || i}`,
      contextKey: c.contextKey || 'region',
      contextValue: c.contextValue ?? '',
    })),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Stable modal height: lock to the Basics tab so switching tabs never resizes ──
  const bodyRef = useRef<HTMLDivElement>(null);
  const [lockedHeight, setLockedHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (lockedHeight == null && tab === 'basics' && bodyRef.current) {
      setLockedHeight(bodyRef.current.scrollHeight);
    }
  }, [lockedHeight, tab]);

  // ── On-behalf-of users (proxy beneficiaries) — async, appended to the picker ──
  const [proxyUsers, setProxyUsers] = useState<ProxyBeneficiarySummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchProxyBeneficiaries()
      .then((list) => { if (!cancelled) setProxyUsers(list); })
      .catch(() => { if (!cancelled) setProxyUsers([]); });
    return () => { cancelled = true; };
  }, []);

  // ── Current user's display name, for the "(Mine)" / "(Me)" self options ──
  const [userName, setUserName] = useState('');
  useEffect(() => {
    let cancelled = false;
    fetchUserByGuid(currentUserGuid)
      .then((u: any) => { if (!cancelled) setUserName(u?.displayName || u?.name || ''); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUserGuid]);
  const meName = userName || t('chronicleSettings.youFallback');

  // ── Owner picker — same population as the Full-Project picker ──
  // (Me + eligible orgs by role/inheritance + sysadmin fast-path) plus on-behalf users.
  const ownerOptions = useMemo<OwnerOption[]>(() => {
    const base = buildOwnerOptions(
      currentUserGuid,
      communityEntities,
      communityRoleAssignments,
      communityEntityRelationships,
      t('chronicleSettings.ownerMyself', { name: meName }),
      isAdmin,
    );
    const meUpper = currentUserGuid.toUpperCase();
    const seen = new Set(base.map((o) => o.value.toUpperCase()));
    // On-behalf-of users (never duplicate "me").
    for (const p of proxyUsers) {
      const g = (p.userGuid || '').toUpperCase();
      if (!g || g === meUpper || seen.has(g)) continue;
      seen.add(g);
      base.push({ value: p.userGuid, label: t('chronicleSettings.ownerOnBehalf', { name: p.displayName }), type: 'user' });
    }
    // Preserve the chronicle's current owner if it isn't otherwise selectable,
    // so editing never silently drops or clobbers an existing owner.
    const curGuid = (chronicle.ownerGuid || '').toString();
    if (curGuid && !seen.has(curGuid.toUpperCase()) && curGuid.toUpperCase() !== meUpper) {
      base.push({ value: curGuid, label: chronicle.ownerDisplayName || curGuid, type: chronicle.ownerType === 'community' ? 'community' : 'user' });
    }
    return base;
  }, [currentUserGuid, communityEntities, communityRoleAssignments, communityEntityRelationships, t, meName, isAdmin, proxyUsers, chronicle.ownerGuid, chronicle.ownerDisplayName, chronicle.ownerType]);

  const selectedOwnerType: 'user' | 'community' = useMemo(() => {
    const match = ownerOptions.find((o) => o.value.toUpperCase() === ownerGuid.toUpperCase());
    return match?.type === 'community' ? 'community' : 'user';
  }, [ownerOptions, ownerGuid]);

  // ── Primary source (the original project) lives on Basics; references on More ──
  const primary = sources.find((s) => s.sourceRole === 'primary') ?? null;
  const references = sources.filter((s) => s.sourceRole === 'reference');

  const upsertPrimary = (patch: Partial<SourceRow>) =>
    setSources((prev) => {
      const idx = prev.findIndex((s) => s.sourceRole === 'primary');
      if (idx === -1) {
        return [
          ...prev,
          { id: `src-${Date.now()}`, sourceRole: 'primary', sourceOrgName: '', sourceTitle: '', sourceUrl: '', permissionStatus: 'linked_reference', ...patch },
        ];
      }
      return prev.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    });

  const addReference = () =>
    setSources((p) => [...p, { id: `src-${Date.now()}-${p.length}`, sourceRole: 'reference', sourceOrgName: '', sourceTitle: '', sourceUrl: '', permissionStatus: 'linked_reference' }]);
  const changeSource = (id: string, patch: Partial<SourceRow>) =>
    setSources((p) => p.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeSource = (id: string) => setSources((p) => p.filter((s) => s.id !== id));

  const addClassification = () =>
    setClassifications((p) => [...p, { id: `cls-${Date.now()}-${p.length}`, dimension: 'solution', valueKey: '' }]);
  const changeClassification = (id: string, patch: Partial<ClassificationRow>) =>
    setClassifications((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeClassification = (id: string) => setClassifications((p) => p.filter((c) => c.id !== id));

  const addContext = () =>
    setContexts((p) => [...p, { id: `ctx-${Date.now()}-${p.length}`, contextKey: 'region', contextValue: '' }]);
  const changeContext = (id: string, patch: Partial<ContextRow>) =>
    setContexts((p) => p.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeContext = (id: string) => setContexts((p) => p.filter((c) => c.id !== id));

  // When an admin flips to "Someone else's", make sure the primary row exists so
  // the credit fields render inline on Basics.
  const handleWhoseChange = (next: 'native' | 'attributed') => {
    setSourceType(next);
    if (next === 'attributed' && !sources.some((s) => s.sourceRole === 'primary')) {
      upsertPrimary({});
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError(t('chronicleEditor.errorTitleRequired'));
      setTab('basics');
      return;
    }
    setSaving(true);
    setError(null);

    const payload: UpdateChronicleRequest = {
      title: title.trim(),
      subtitle: subtitle.trim() || undefined,
      status,
      visibility,
    };

    // Ownership — the picker preserves the current owner as a fallback option,
    // so sending it is always safe (the server re-checks community authority).
    if (ownerGuid) {
      payload.ownerGuid = ownerGuid;
      payload.ownerType = selectedOwnerType;
    }

    // Attribution is admin/curator-only (spec §2) — only admins send these.
    if (isAdmin) {
      const isAttributed = sourceType === 'attributed';
      payload.sourceType = sourceType;
      payload.sources = isAttributed
        ? sources
            .filter((s) => s.sourceOrgName.trim() || s.sourceUrl.trim() || s.sourceTitle.trim())
            .map((s, i) => ({
              sourceRole: s.sourceRole,
              sourceOrgName: s.sourceOrgName.trim() || undefined,
              sourceTitle: s.sourceTitle.trim() || undefined,
              sourceUrl: s.sourceUrl.trim() || undefined,
              permissionStatus: s.permissionStatus || undefined,
              position: i,
            }))
        : [];
      payload.classifications = isAttributed
        ? classifications.filter((c) => c.valueKey.trim()).map((c, i) => ({ dimension: c.dimension, valueKey: c.valueKey.trim(), position: i }))
        : [];
      payload.contexts = isAttributed
        ? contexts.filter((c) => c.contextValue.trim()).map((c, i) => ({ contextKey: c.contextKey, contextValue: c.contextValue.trim(), position: i }))
        : [];
    }

    try {
      await updateChronicle(chronicle.chronicleGuid, payload);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Save failed');
      setSaving(false);
    }
  };

  // ── Styles ──
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const card: React.CSSProperties = {
    width: 'min(620px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 12,
    border: '1px solid var(--border-default)', backgroundColor: 'var(--bg-primary)',
    backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))', boxShadow: 'var(--shadow-lg)',
  };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 13, color: 'var(--text-tertiary)', marginBlockEnd: 6 };
  const helpStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', marginBlockStart: 6, lineHeight: 1.5 };
  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '8px 11px', borderRadius: 8, border: '1px solid var(--border-default)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14,
  };
  const rowStyle: React.CSSProperties = { display: 'flex', gap: 8, marginBlockEnd: 8, flexWrap: 'wrap', alignItems: 'center' };
  const cellStyle: React.CSSProperties = { ...fieldStyle, width: 'auto', flex: '1 1 140px', minWidth: 0 };
  const addBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--accent-border)', background: 'var(--accent-bg)', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
  const removeBtn: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 12, cursor: 'pointer' };
  const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBlockEnd: 4 };
  const tabBtn = (active: boolean): React.CSSProperties => ({
    padding: '8px 14px', borderRadius: 8, border: 0, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    background: active ? 'var(--accent-bg)' : 'transparent',
    color: active ? 'var(--accent-text)' : 'var(--text-tertiary)',
  });

  const tabs: Array<{ key: TabKey; label: string }> = [
    { key: 'basics', label: t('chronicleSettings.tabBasics') },
    { key: 'visibility', label: t('chronicleSettings.tabVisibility') },
    ...(isAdmin ? [{ key: 'more' as TabKey, label: t('chronicleSettings.tabMore') }] : []),
  ];

  // ── Visibility option helper ──
  const visOption = (value: string, label: string, help: string) => (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, border: `1px solid ${visibility === value ? 'var(--accent-border)' : 'var(--border-default)'}`, cursor: 'pointer', marginBlockEnd: 8 }}>
      <input type="radio" name="cs-visibility" checked={visibility === value} onChange={() => setVisibility(value)} style={{ marginBlockStart: 3 }} />
      <span>
        <span style={{ display: 'block', fontSize: 14, color: 'var(--text-primary)', fontWeight: 600 }}>{label}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBlockStart: 2, lineHeight: 1.5 }}>{help}</span>
      </span>
    </label>
  );

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>{t('chronicleSettings.heading')}</h2>
          <button type="button" onClick={onClose} aria-label={t('chronicleSettings.cancel')} style={{ background: 'transparent', border: 0, color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 4, padding: '10px 14px', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          {tabs.map((tb) => (
            <button key={tb.key} type="button" style={tabBtn(tab === tb.key)} onClick={() => setTab(tb.key)}>{tb.label}</button>
          ))}
        </div>

        {/* Body — height locked to the Basics tab so tab switches never resize the modal. */}
        <div ref={bodyRef} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', height: lockedHeight ?? 'auto' }}>
          {/* ── BASICS ── */}
          {tab === 'basics' && (
            <>
              <div>
                <label style={labelStyle} htmlFor="cs-title">{t('chronicleEditor.titleLabel')}</label>
                <input id="cs-title" style={fieldStyle} value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle} htmlFor="cs-subtitle">{t('chronicleSettings.subtitleLabel')}</label>
                <input id="cs-subtitle" style={fieldStyle} value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle} htmlFor="cs-status">{t('chronicleSettings.statusLabel')}</label>
                <select id="cs-status" style={fieldStyle} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="draft">{t('chronicleSettings.statusDraft')}</option>
                  <option value="published">{t('chronicleSettings.statusPublished')}</option>
                  <option value="archived">{t('chronicleSettings.statusArchived')}</option>
                </select>
              </div>

              {/* Ownership — Me + eligible organizations + on-behalf-of users (same as Projects). */}
              <div>
                <label style={labelStyle} htmlFor="cs-owner">{t('chronicleSettings.ownerLabel')}</label>
                <select id="cs-owner" style={fieldStyle} value={ownerGuid} onChange={(e) => setOwnerGuid(e.target.value)}>
                  {ownerOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div style={helpStyle}>{t('chronicleSettings.ownerHelp')}</div>
              </div>

              {/* Crediting — who did the real-world work (distinct from Owner above).
                  Admin/curator only for now; opens to students with the listing/curation model (DEV-1347). */}
              {isAdmin && (
                <div style={{ borderBlockStart: '1px solid var(--border-subtle)', paddingBlockStart: 14 }}>
                  <div style={sectionTitle}>{t('chronicleSettings.creditingHeading')}</div>
                  <label style={labelStyle} htmlFor="cs-whose">{t('chronicleSettings.whoseLabel')}</label>
                  <select id="cs-whose" style={fieldStyle} value={sourceType} onChange={(e) => handleWhoseChange(e.target.value === 'attributed' ? 'attributed' : 'native')}>
                    <option value="native">{t('chronicleSettings.whoseMine', { name: meName })}</option>
                    <option value="attributed">{t('chronicleSettings.whoseSomeoneElse')}</option>
                  </select>
                  <div style={helpStyle}>{t('chronicleSettings.whoseHelp')}</div>

                  {sourceType === 'attributed' && (
                    <div style={{ marginBlockStart: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div>
                        <label style={labelStyle}>{t('chronicleSettings.origNameLabel')}</label>
                        <input style={fieldStyle} value={primary?.sourceOrgName ?? ''} onChange={(e) => upsertPrimary({ sourceOrgName: e.target.value })} placeholder={t('chronicleSettings.origNamePlaceholder')} />
                      </div>
                      <div>
                        <label style={labelStyle}>{t('chronicleSettings.origLinkLabel')}</label>
                        <input type="url" style={fieldStyle} value={primary?.sourceUrl ?? ''} onChange={(e) => upsertPrimary({ sourceUrl: e.target.value })} placeholder={t('chronicleSettings.origLinkPlaceholder')} />
                      </div>
                      <div>
                        <label style={labelStyle}>{t('chronicleSettings.permLabel')}</label>
                        <select style={fieldStyle} value={primary?.permissionStatus ?? 'linked_reference'} onChange={(e) => upsertPrimary({ permissionStatus: e.target.value })}>
                          <option value="linked_reference">{t('chronicleSettings.permLinked')}</option>
                          <option value="permission_requested">{t('chronicleSettings.permRequested')}</option>
                          <option value="granted">{t('chronicleSettings.permGranted')}</option>
                          <option value="declined">{t('chronicleSettings.permDeclined')}</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── Danger zone: soft-delete (owner/sysadmin) ── */}
              {canDelete && (
                <div style={{ borderBlockStart: '1px solid var(--color-error)', paddingBlockStart: 14, marginBlockStart: 4 }}>
                  <div style={{ ...sectionTitle, color: 'var(--color-error)' }}>{t('chronicleSettings.dangerHeading')}</div>
                  <div style={{ ...helpStyle, marginBlockStart: 0, marginBlockEnd: 10 }}>{t('chronicleSettings.dangerHelp')}</div>
                  {!confirmDelete ? (
                    <button
                      type="button"
                      onClick={() => { setConfirmDelete(true); setDelText(''); }}
                      style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-error)', background: 'transparent', color: 'var(--color-error)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                    >
                      {t('chronicleSettings.deleteButton')}
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <label style={labelStyle}>{t('chronicleSettings.deleteConfirmLabel', { title: chronicle.title })}</label>
                      <input style={fieldStyle} value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={chronicle.title} autoFocus />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => { setConfirmDelete(false); setDelText(''); }}
                          disabled={deleting}
                          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                        >
                          {t('chronicleSettings.cancel')}
                        </button>
                        <button
                          type="button"
                          onClick={handleSoftDelete}
                          disabled={deleting || delText.trim() !== chronicle.title.trim()}
                          style={{
                            padding: '8px 14px', borderRadius: 8, border: 0, background: 'var(--color-error)', color: '#fff', fontWeight: 700, fontSize: 13,
                            cursor: (deleting || delText.trim() !== chronicle.title.trim()) ? 'default' : 'pointer',
                            opacity: (deleting || delText.trim() !== chronicle.title.trim()) ? 0.4 : 1,
                          }}
                        >
                          {deleting ? t('chronicleSettings.deleting') : t('chronicleSettings.deleteConfirmButton')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ── WHO CAN SEE THIS ── */}
          {tab === 'visibility' && (
            <div>
              <label style={labelStyle}>{t('chronicleSettings.visibilityLabel')}</label>
              {visOption('public', t('chronicleSettings.visPublic'), t('chronicleSettings.visPublicHelp'))}
              {visOption('community', t('chronicleSettings.visCommunity'), t('chronicleSettings.visCommunityHelp'))}
              {visOption('private', t('chronicleSettings.visPrivate'), t('chronicleSettings.visPrivateHelp'))}
            </div>
          )}

          {/* ── MORE (admin) ── */}
          {tab === 'more' && isAdmin && (
            sourceType !== 'attributed' ? (
              <div style={helpStyle}>{t('chronicleSettings.moreNativeNote')}</div>
            ) : (
              <>
                <div style={helpStyle}>{t('chronicleSettings.moreIntro')}</div>

                {/* Topics (discovery classification) */}
                <div>
                  <div style={sectionTitle}>{t('chronicleSettings.topicsLabel')}</div>
                  <div style={{ ...helpStyle, marginBlockStart: 0, marginBlockEnd: 8 }}>{t('chronicleSettings.topicsHelp')}</div>
                  {classifications.map((c) => (
                    <div key={c.id} style={rowStyle}>
                      <select style={{ ...cellStyle, flex: '0 0 150px' }} value={c.dimension} onChange={(e) => changeClassification(c.id, { dimension: e.target.value as ClassificationRow['dimension'] })}>
                        <option value="solution">{t('chronicleSettings.dimSolution')}</option>
                        <option value="problem">{t('chronicleSettings.dimProblem')}</option>
                        <option value="issue">{t('chronicleSettings.dimIssue')}</option>
                        <option value="outcome">{t('chronicleSettings.dimOutcome')}</option>
                      </select>
                      <input style={cellStyle} value={c.valueKey} onChange={(e) => changeClassification(c.id, { valueKey: e.target.value })} placeholder={t('chronicleSettings.topicPlaceholder')} />
                      <button type="button" style={removeBtn} onClick={() => removeClassification(c.id)}>{t('chronicleSettings.remove')}</button>
                    </div>
                  ))}
                  <button type="button" style={addBtn} onClick={addClassification}>{t('chronicleSettings.addTopic')}</button>
                </div>

                {/* Where it happened (local conditions) */}
                <div style={{ borderBlockStart: '1px solid var(--border-subtle)', paddingBlockStart: 14 }}>
                  <div style={sectionTitle}>{t('chronicleSettings.whereLabel')}</div>
                  <div style={{ ...helpStyle, marginBlockStart: 0, marginBlockEnd: 8 }}>{t('chronicleSettings.whereHelp')}</div>
                  {contexts.map((c) => (
                    <div key={c.id} style={rowStyle}>
                      <select style={{ ...cellStyle, flex: '0 0 150px' }} value={c.contextKey} onChange={(e) => changeContext(c.id, { contextKey: e.target.value })}>
                        <option value="region">{t('chronicleSettings.ctxRegion')}</option>
                        <option value="climate">{t('chronicleSettings.ctxClimate')}</option>
                        <option value="urban_rural">{t('chronicleSettings.ctxUrbanRural')}</option>
                        <option value="scale">{t('chronicleSettings.ctxScale')}</option>
                        <option value="demographics">{t('chronicleSettings.ctxDemographics')}</option>
                        <option value="other">{t('chronicleSettings.ctxOther')}</option>
                      </select>
                      <input style={cellStyle} value={c.contextValue} onChange={(e) => changeContext(c.id, { contextValue: e.target.value })} placeholder={t('chronicleSettings.wherePlaceholder')} />
                      <button type="button" style={removeBtn} onClick={() => removeContext(c.id)}>{t('chronicleSettings.remove')}</button>
                    </div>
                  ))}
                  <button type="button" style={addBtn} onClick={addContext}>{t('chronicleSettings.addWhere')}</button>
                </div>

                {/* More references (reference sources) */}
                <div style={{ borderBlockStart: '1px solid var(--border-subtle)', paddingBlockStart: 14 }}>
                  <div style={sectionTitle}>{t('chronicleSettings.referencesLabel')}</div>
                  <div style={{ ...helpStyle, marginBlockStart: 0, marginBlockEnd: 8 }}>{t('chronicleSettings.referencesHelp')}</div>
                  {references.map((s) => (
                    <div key={s.id} style={rowStyle}>
                      <input style={cellStyle} value={s.sourceOrgName} onChange={(e) => changeSource(s.id, { sourceOrgName: e.target.value })} placeholder={t('chronicleSettings.refNamePlaceholder')} />
                      <input style={cellStyle} type="url" value={s.sourceUrl} onChange={(e) => changeSource(s.id, { sourceUrl: e.target.value })} placeholder={t('chronicleSettings.refLinkPlaceholder')} />
                      <button type="button" style={removeBtn} onClick={() => removeSource(s.id)}>{t('chronicleSettings.remove')}</button>
                    </div>
                  ))}
                  <button type="button" style={addBtn} onClick={addReference}>{t('chronicleSettings.addReference')}</button>
                </div>
              </>
            )
          )}

          {error && <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 18px', borderBlockStart: '1px solid var(--border-subtle)' }}>
          <button type="button" onClick={onClose} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>{t('chronicleSettings.cancel')}</button>
          <button type="button" onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: 'var(--accent-primary)', color: '#fff', cursor: saving ? 'wait' : 'pointer', fontWeight: 600 }}>{t('chronicleSettings.save')}</button>
        </div>
      </div>
    </div>
  );
}
